import React, { useState } from 'react';
import Sidebar from './Sidebar';
import { FiWifi, FiRefreshCcw } from 'react-icons/fi';
import type { WirelessDevice } from '@/types/deviceSetup';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { isValidWirelessUuid } from '@/utils/wirelessSwitch';

interface UnpairedAddonsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  devices: WirelessDevice[];
  isWirelessButtonSupported?: boolean;
  onRefresh?: () => void | Promise<void>;
  onPairDevice?: (device: WirelessDevice) => void | Promise<void>;
}

const getSignalClass = (rssi?: number) => {
  if (typeof rssi !== 'number') return 'text-gray-400';
  return rssi >= -60 ? 'text-green-600' : rssi >= -70 ? 'text-yellow-500' : 'text-red-500';
};

const UnpairedAddonsSidebar: React.FC<UnpairedAddonsSidebarProps> = ({
  isOpen,
  onClose,
  devices,
  isWirelessButtonSupported = false,
  onRefresh,
  onPairDevice,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<WirelessDevice | null>(null);
  const validDevices = devices.filter((device) => isValidWirelessUuid(device.uuid));
  const unpairedExpanders = validDevices.filter((device) => device.type !== 1);
  const unpairedButtons = validDevices.filter((device) => device.type === 1);

  const handleClick = (d: WirelessDevice) => {
    setSelectedDevice(d);
    setConfirmOpen(true);
  };
  
  const handleConfirm = async () => {
    if (selectedDevice && onPairDevice) {
      await onPairDevice(selectedDevice);
    }
    setConfirmOpen(false);
  };
  
  const handleCancel = () => {
    setConfirmOpen(false);
  };

  const renderWirelessButtonsEmptyState = () => (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-5 py-8 text-center">
      <div
        className="mx-auto flex h-[70px] w-[70px] items-center justify-center rounded-full border"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.10)',
          borderColor: 'rgba(137, 138, 141, 0.20)',
        }}
      >
        <img
          src="/ic_wireless_buttons.png"
          alt="Wireless buttons"
          className="h-10 w-10 object-contain opacity-80 dark:opacity-70"
        />
      </div>
      <div className="mt-8 text-lg font-semibold text-gray-900 dark:text-gray-100">
        No Buttons Found
      </div>
      <div className="mt-2 text-base text-gray-700 dark:text-gray-200">
        Make sure your wireless button is:
      </div>
      <div className="mx-auto mt-3 max-w-[260px] whitespace-pre-line text-sm leading-6 text-gray-500 dark:text-gray-400">
        {'• Powered on with fresh batteries\n• Within range of the controller\n• In pairing mode (press and hold)'}
      </div>
    </div>
  );

  return (
    <Sidebar isOpen={isOpen} onClose={onClose} title="Unpaired Add-ons">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-300">Select a device to pair</div>
          <button
            type="button"
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            onClick={onRefresh}
            aria-label="Refresh"
          >
            <FiRefreshCcw className="h-4 w-4" /> Refresh
          </button>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Discovered Wireless Expanders
            </div>
            {unpairedExpanders.length === 0 ? (
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-5 py-6">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-200">No wireless expanders found.</div>
              </div>
            ) : (
              unpairedExpanders.map((d) => (
                <button
                  key={d.uuid}
                  type="button"
                  className="w-full p-3 rounded-md border border-gray-300 dark:border-gray-700 flex items-center justify-between bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800"
                  onClick={() => handleClick(d)}
                >
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[70%]">
                    {d.uuid || '-'}
                  </span>
                  <span className="inline-flex items-center gap-1 whitespace-nowrap">
                    <FiWifi className={getSignalClass(d.rssi)} />
                    <span className={`text-xs ${getSignalClass(d.rssi)}`}>{d.rssi} dBm</span>
                  </span>
                </button>
              ))
            )}
          </div>

          {isWirelessButtonSupported && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Discovered Wireless Buttons
              </div>
              {unpairedButtons.length === 0 ? (
                renderWirelessButtonsEmptyState()
              ) : (
                unpairedButtons.map((d) => (
                  <button
                    key={d.uuid}
                    type="button"
                    className="w-full p-3 rounded-md border border-gray-300 dark:border-gray-700 flex items-center justify-between bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => handleClick(d)}
                  >
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[70%]">
                      Wireless Button - {d.uuid || '-'}
                    </span>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <FiWifi className={getSignalClass(d.rssi)} />
                      <span className={`text-xs ${getSignalClass(d.rssi)}`}>{d.rssi} dBm</span>
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>
      
      <ConfirmDialog
        open={confirmOpen}
        title="Pair Device"
        onCancel={handleCancel}
        onOk={handleConfirm}
        cancelText="Cancel"
        okText="Pair"
      >
        <p>Are you sure you want to pair this device?</p>
        {selectedDevice && (
          <p className="mt-2 font-medium">
            {selectedDevice.type === 1 ? 'Wireless Button' : 'Power Expander'} - {selectedDevice.uuid || '-'}
          </p>
        )}
      </ConfirmDialog>
    </Sidebar>
  );
};

export default UnpairedAddonsSidebar;
